import React, { useState, useEffect } from 'react';
import { Truck, CreditCard, RotateCcw, AlertCircle, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { InfoPageHeader } from '@/components/brand/InfoPageHeader';

const TermsAndConditions = () => {
  // Initialize language from localStorage or default to Latvian
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language') || localStorage.getItem('selectedLanguage');
    return savedLanguage || 'lv';
  });

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    en: {
      title: "Terms and Conditions",
      subtitle: "Delivery and Service Terms",
      delivery: {
        title: "Delivery",
        items: [
          "Free delivery on orders over €35 within Liepāja.",
          "Average delivery time: 30 min - 1 hour.",
          "Pickup discount: 10%.",
          "Delivery is only made to the address specified in the order.",
          "During high-demand periods (Friday, weekends), delivery time may be extended."
        ]
      },
      payment: {
        title: "Payment",
        items: [
          "Payment in cash to the courier or online on the website.",
          "All prices are in euros, including VAT."
        ]
      },
      returns: {
        title: "Return Policy",
        items: [
          "If the pizza or dish was damaged during delivery, contact us at +371 28717331 or rozedinapoliorders@gmail.com within 24 hours.",
          "Refund or compensation is possible only upon presentation of a photo and description of the situation.",
          "Complaints about the quality of custom-made dishes are considered individually."
        ]
      },
      age: {
        title: "Age Restrictions",
        items: [
          "Sale and delivery of alcoholic beverages is permitted only to persons over 18 years of age.",
          "When receiving alcoholic products, it is necessary to present an identity document confirming age.",
          "Alcohol delivery is prohibited from 22:00 to 8:00."
        ]
      },
      contact: {
        title: "Contact Information",
        address: "Liepu iela 9, Liepāja, LV-3401",
        phone: "+371 28717331",
        email: "rozedinapoliorders@gmail.com",
        hours: "Mon–Fri 11:00–23:00 | Sat–Sun 12:00–00:00"
      },
      copyright: "All rights reserved."
    },
    ru: {
      title: "Правила и условия",
      subtitle: "Условия доставки и обслуживания",
      delivery: {
        title: "Доставка",
        items: [
          "Бесплатная доставка при заказе от 35 € в пределах Лиепаи.",
          "Среднее время доставки — 30 мин - 1 час.",
          "Самовывоз — скидка 10 %.",
          "Доставка осуществляется только по адресу, указанному при заказе.",
          "В случае повышенной нагрузки (пятница, выходные) время доставки может быть увеличено."
        ]
      },
      payment: {
        title: "Оплата",
        items: [
          "Оплата наличными курьеру или онлайн на сайте.",
          "Все цены указаны в евро, включая НДС."
        ]
      },
      returns: {
        title: "Право на возврат",
        items: [
          "Если пицца или блюдо были повреждены во время доставки — свяжитесь с нами по телефону +371 28717331 или rozedinapoliorders@gmail.com в течение 24 часов.",
          "Возврат или компенсация возможны только при предъявлении фото и описания ситуации.",
          "Жалобы на качество блюд, приготовленных по индивидуальному заказу, рассматриваются индивидуально."
        ]
      },
      age: {
        title: "Возрастные ограничения",
        items: [
          "Продажа и доставка алкогольных напитков разрешена только лицам старше 18 лет.",
          "При получении алкогольной продукции необходимо предъявить документ, подтверждающий возраст.",
          "Доставка алкоголя запрещена с 22:00 до 8:00."
        ]
      },
      contact: {
        title: "Контактная информация",
        address: "Liepu iela 9, Liepāja, LV-3401",
        phone: "+371 28717331",
        email: "rozedinapoliorders@gmail.com",
        hours: "Пн–Пт 11:00–23:00 | Сб–Вс 12:00–00:00"
      },
      copyright: "Все права защищены."
    },
    lv: {
      title: "Noteikumi un nosacījumi",
      subtitle: "Piegādes un apkalpošanas noteikumi",
      delivery: {
        title: "Piegāde",
        items: [
          "Bezmaksas piegāde pasūtījumiem virs €35 Liepājā.",
          "Vidējais piegādes laiks: 30 min - 1 stunda.",
          "Līdzņemšanas atlaide: 10%.",
          "Piegāde tiek veikta tikai uz pasūtījumā norādīto adresi.",
          "Augstas pieprasījuma periodos (piektdiena, brīvdienas) piegādes laiks var tikt pagarināts."
        ]
      },
      payment: {
        title: "Maksājums",
        items: [
          "Maksājums skaidrā naudā kurjeram vai tiešsaistē vietnē.",
          "Visas cenas ir norādītas eiro, ieskaitot PVN."
        ]
      },
      returns: {
        title: "Atgriešanas politika",
        items: [
          "Ja pica vai ēdiens tika bojāts piegādes laikā, sazinieties ar mums pa tālruni +371 28717331 vai rozedinapoliorders@gmail.com 24 stundu laikā.",
          "Atmaksa vai kompensācija ir iespējama tikai uzrādot fotoattēlu un situācijas aprakstu.",
          "Sūdzības par pēc individuāla pasūtījuma gatavotu ēdienu kvalitāti tiek izskatītas individuāli."
        ]
      },
      age: {
        title: "Vecuma ierobežojumi",
        items: [
          "Alkoholisko dzērienu pārdošana un piegāde ir atļauta tikai personām, kas vecākas par 18 gadiem.",
          "Saņemot alkoholiskos produktus, ir jāuzrāda personu apliecinošs dokuments, kas apstiprina vecumu.",
          "Alkohola piegāde ir aizliegta no 22:00 līdz 8:00."
        ]
      },
      contact: {
        title: "Kontaktinformācija",
        address: "Liepu iela 9, Liepāja, LV-3401",
        phone: "+371 28717331",
        email: "rozedinapoliorders@gmail.com",
        hours: "P.–Pk. 11:00–23:00 | S.–Sv. 12:00–00:00"
      },
      copyright: "Visas tiesības aizsargātas."
    }
  };

  const t = content[language];

  return (
    <div className="rz-page min-h-screen" data-testid="terms-page">
      <InfoPageHeader language={language} setLanguage={setLanguage} title={t.title} subtitle={t.subtitle} />

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-6">
          
          {/* Delivery Section */}
          <div className="bg-white border border-[#E7E5E4]">
            <div className="p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                    <Truck className="w-6 h-6 text-[#1C1917]" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                    {t.delivery.title}
                  </h2>
                  <ul className="space-y-3">
                    {t.delivery.items.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-[#1C1917] mr-2">•</span>
                        <span className="text-[#57534E]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="bg-white border border-[#E7E5E4]">
            <div className="p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-[#1C1917]" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                    {t.payment.title}
                  </h2>
                  <ul className="space-y-3">
                    {t.payment.items.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-[#1C1917] mr-2">•</span>
                        <span className="text-[#57534E]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Returns Section */}
          <div className="bg-white border border-[#E7E5E4]">
            <div className="p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                    <RotateCcw className="w-6 h-6 text-[#1C1917]" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                    {t.returns.title}
                  </h2>
                  <ul className="space-y-3">
                    {t.returns.items.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-[#1C1917] mr-2">•</span>
                        <span className="text-[#57534E]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Age Restrictions Section */}
          <div className="bg-white border border-[#E7E5E4]">
            <div className="p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-[#C2410C]" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                    {t.age.title}
                  </h2>
                  <ul className="space-y-3">
                    {t.age.items.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-[#C2410C] mr-2">•</span>
                        <span className="text-[#57534E]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-[#F5F2EC] border border-[#E7E5E4] overflow-hidden">
            <div className="p-8">
              <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-6 text-center">
                {t.contact.title}
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-2 text-[#57534E]">
                  <MapPin className="w-5 h-5 text-[#1C1917]" />
                  <span>{t.contact.address}</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <Phone className="w-5 h-5 text-[#1C1917]" />
                  <a href="tel:+37128717331" className="text-[#57534E] hover:text-[#1C1917]">
                    {t.contact.phone}
                  </a>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <Mail className="w-5 h-5 text-[#C2410C]" />
                  <a href="mailto:rozedinapoliorders@gmail.com" className="text-[#57534E] hover:text-[#1C1917]">
                    {t.contact.email}
                  </a>
                </div>
                <div className="flex items-center justify-center space-x-2 text-[#57534E]">
                  <Clock className="w-5 h-5 text-[#1C1917]" />
                  <span>{t.contact.hours}</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Copyright */}
        <div className="mt-8 text-center text-[#57534E]">
          <p>© 2025 Roze di Napoli. {t.copyright}</p>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <a 
            href="/" 
            className="inline-flex items-center text-[#57534E] hover:text-[#1C1917] transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
